import React from "react";
import type { FAQPage, WithContext } from "schema-dts";

import type { HeadingBlock, AccordionBlock as Value } from "@reactivated";

import {
    StreamField,
    type StreamFieldBlockMap,
} from "@thelabnyc/thelabui/src/components/StreamField";

import { Themed } from "../../styles/themes";
import { RichTextStreamBlock } from "../RichTextStreamBlock";
import { SpacerBlock } from "../SpacerBlock";
import { AccordionGroup } from "./AccordionGroup";
import type { HeadingTag } from "./DisclosureRow";

export const FALLBACK_HEADING: HeadingTag = "h3";

export const NEXT_HEADING: Record<
    NonNullable<HeadingBlock["tag"]>,
    HeadingTag
> = {
    h1: "h2",
    h2: "h3",
    h3: "h4",
    h4: "h5",
    h5: "h6",
    h6: "h6",
    p: FALLBACK_HEADING,
};

/**
 * A lil bit of cleverness to give accordion titles the proper heading level
 * when this block is generally being used the way it's supposed to be.
 */
export const findAccordionHeadingTag = (
    items: NonNullable<Value["items"]>,
): HeadingTag => {
    const accordionIndex = items.findIndex((i) => i.type === "accordion");
    if (accordionIndex === -1) return FALLBACK_HEADING;

    // Grab any headings that are before the accordion
    const headings = items
        .slice(0, accordionIndex)
        .flatMap((item) =>
            item.type === "rich_text_stream" ? (item.value.content ?? []) : [],
        )
        .filter((inner) => inner.type === "heading");
    // Look at the one closest to the accordion
    const lastHeading = headings[headings.length - 1];

    // If there's a heading, use its tag to go one heading level down for accordion
    return lastHeading?.value.tag
        ? NEXT_HEADING[lastHeading.value.tag]
        : FALLBACK_HEADING;
};

export const AccordionBlock = ({ value }: { value: Value }) => {
    if (!value.items) return <></>;
    const headingTag = findAccordionHeadingTag(value.items);
    const components: StreamFieldBlockMap<NonNullable<Value["items"]>> = {
        rich_text_stream: RichTextStreamBlock,
        spacer: SpacerBlock,
        accordion: (props) => (
            <AccordionGroup value={props.value} headingTag={headingTag} />
        ),
    };
    const questions = value.use_faq_schema
        ? value.items.flatMap((item) =>
              item.type === "accordion"
                  ? (item.value.items ?? []).flatMap((entry) =>
                        entry.title && entry.details
                            ? [
                                  {
                                      "@type": "Question" as const,
                                      "name": entry.title,
                                      "acceptedAnswer": {
                                          "@type": "Answer" as const,
                                          "text": entry.details,
                                      },
                                  },
                              ]
                            : [],
                    )
                  : [],
          )
        : [];
    const jsonLd: WithContext<FAQPage> = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": questions,
    };
    return (
        <Themed theme={value.theme} tag="section">
            {questions.length > 0 && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c"),
                    }}
                />
            )}
            <StreamField
                tagName={React.Fragment}
                components={components}
                value={value.items}
            />
        </Themed>
    );
};
